/**
 * AgentCostOverTimeChart - Stacked bar chart showing cost/tokens over time by agent
 *
 * Features:
 * - Stacked bar chart with top 10 non-zero agents (no "Other" bucket)
 * - Supports 5 metrics: cost, total_tokens, prompt_tokens, completion_tokens, cached_tokens
 * - Agent-specific colors
 * - Dynamic Y-axis formatting based on metric
 * - Click-to-drill navigation to Traces page
 * - Built with Recharts for reliable click handling
 */

import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { palette } from '../../utils/designTokens';
import { getChartColors } from '../../utils/chartColors';
import { serializeFilters } from '../../utils/urlBuilder';
import { buildCostDrilldownWindow, parseCostBucketTime } from '../../utils/costDrilldown';

/**
 * Format timestamp for X-axis display
 */
const formatTimestamp = (timestamp, timeWindow) => {
  if (!timestamp) return '';

  try {
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return '';

    // For 24h view, show time (HH:MM)
    if (timeWindow === '24h') {
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
    }

    // For 7d/30d/90d, show date (MM/DD)
    return date.toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
    });
  } catch {
    return '';
  }
};

/**
 * Format Y-axis ticks based on metric type
 */
const formatYAxis = (value, metric) => {
  if (metric === 'cost_usd') {
    // Format as dollars
    if (value >= 1000) return `$${(value / 1000).toFixed(1)}K`;
    if (value >= 1) return `$${value.toFixed(0)}`;
    if (value >= 0.01) return `$${value.toFixed(2)}`;
    if (value > 0) return `$${value.toFixed(4)}`;
    return '$0';
  } else {
    // Format as tokens (K or M)
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
    return value.toFixed(0);
  }
};

/**
 * Format tooltip value based on metric type
 */
const formatTooltipValue = (value, metric) => {
  if (metric === 'cost_usd') {
    return `$${value.toFixed(4)}`;
  } else {
    return value.toLocaleString();
  }
};

/**
 * Process raw API data for agent time-series chart
 */
const processChartData = (agentCostOverTimeData, selectedMetric) => {
  if (!agentCostOverTimeData || agentCostOverTimeData.length === 0) {
    return { chartData: {}, agentList: [] };
  }

  // Step 1: Calculate total per agent across all timestamps
  const agentTotals = {};

  agentCostOverTimeData.forEach(({ agents }) => {
    agents.forEach((agent) => {
      const agentName = agent.agent_name;
      let value = 0;

      if (selectedMetric === 'cost_usd') {
        value = agent.cost_usd || 0;
      } else if (selectedMetric === 'total_tokens') {
        value = agent.total_tokens || 0;
      } else if (selectedMetric === 'prompt_tokens') {
        value = agent.prompt_tokens || 0;
      } else if (selectedMetric === 'completion_tokens') {
        value = agent.completion_tokens || 0;
      } else if (selectedMetric === 'cached_tokens') {
        value = agent.cached_tokens || 0;
      }

      agentTotals[agentName] = (agentTotals[agentName] || 0) + value;
    });
  });

  // Step 2: Filter out agents with 0 total, then get top 10 sorted by total (descending)
  const sortedAgents = Object.entries(agentTotals)
    .filter(([, total]) => total > 0) // Only include agents with non-zero values
    .sort(([, a], [, b]) => b - a)
    .map(([name]) => name);

  const top10Agents = sortedAgents.slice(0, 10);

  // Step 3: Build chart data format - separate array per agent
  const chartData = {};

  top10Agents.forEach((agentName) => {
    chartData[agentName] = [];
  });

  // Step 4: Populate data arrays
  agentCostOverTimeData.forEach(({ timestamp, agents }) => {
    top10Agents.forEach((agentName) => {
      const agent = agents.find((a) => a.agent_name === agentName);
      let value = 0;

      if (agent) {
        if (selectedMetric === 'cost_usd') {
          value = agent.cost_usd || 0;
        } else if (selectedMetric === 'total_tokens') {
          value = agent.total_tokens || 0;
        } else if (selectedMetric === 'prompt_tokens') {
          value = agent.prompt_tokens || 0;
        } else if (selectedMetric === 'completion_tokens') {
          value = agent.completion_tokens || 0;
        } else if (selectedMetric === 'cached_tokens') {
          value = agent.cached_tokens || 0;
        }
      }

      chartData[agentName].push({ x: parseCostBucketTime(timestamp), y: value });
    });
  });

  // Step 5: Build agent list (top 10 non-zero agents)
  const agentList = [...top10Agents];

  return { chartData, agentList };
};

/**
 * AgentCostOverTimeChart Component
 */
const AgentCostOverTimeChart = ({ data, selectedMetric, timeWindow, appId, environment }) => {
  const navigate = useNavigate();
  const [highlightedAgent, setHighlightedAgent] = useState(null);

  // Process data
  const { chartData, agentList } = useMemo(
    () => processChartData(data, selectedMetric),
    [data, selectedMetric]
  );

  // Memoize agent colors - compute once per agentList change
  const agentColors = useMemo(() => {
    return getChartColors(agentList);
  }, [agentList]);

  // Transform data for Recharts (needs flat array with all agents as keys)
  // MUST be declared BEFORE any early returns to satisfy Rules of Hooks
  const rechartData = useMemo(() => {
    if (!chartData || agentList.length === 0) return [];

    // Get all unique timestamps
    const timestampSet = new Set();
    agentList.forEach(agentName => {
      chartData[agentName]?.forEach(point => {
        timestampSet.add(point.x.getTime());
      });
    });

    const timestamps = Array.from(timestampSet).sort((a, b) => a - b);

    return timestamps.map(ts => {
      const dataPoint = { timestamp: new Date(ts) };
      agentList.forEach(agentName => {
        const point = chartData[agentName]?.find(p => p.x.getTime() === ts);
        dataPoint[agentName] = point ? point.y : 0;
      });
      return dataPoint;
    });
  }, [chartData, agentList]);

  // Check if we have data (must compute after all hooks)
  const hasData = agentList.length > 0 && Object.keys(chartData).length > 0;

  const containerRef = React.useRef(null);

  if (!hasData) {
    return (
      <div
        style={{
          height: 300,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: palette.text.muted,
          fontSize: '13px',
        }}
      >
        <p>No cost data available</p>
        <p style={{ fontSize: '11px', marginTop: '4px' }}>Try adjusting your time range</p>
      </div>
    );
  }

  // Handle bar click for drilldown to Agents page
  const handleBarClick = (agentName, data) => {
    if (!agentName || !data || !data.timestamp) {
      return;
    }

    // Highlight the clicked agent
    setHighlightedAgent(agentName);

    const { start_time: startTimeStr, end_time: endTimeStr } =
      buildCostDrilldownWindow(data.timestamp, timeWindow);

    // Map selectedMetric to sort parameter
    const sortMapping = {
      'cost': 'cost_desc',
      'total_tokens': 'total_tokens_desc',
      'prompt_tokens': 'prompt_tokens_desc',
      'completion_tokens': 'completion_tokens_desc',
      'cached_tokens': 'cached_tokens_desc'
    };
    const sortParam = sortMapping[selectedMetric] || 'cost_desc';

    // Navigate to Agents page with filters after a brief highlight
    setTimeout(() => {
      setHighlightedAgent(null);
      const filters = {
        agent: agentName,
        start_time: startTimeStr,
        end_time: endTimeStr,
        sort: sortParam,
        env: environment,
        range: timeWindow,
        from: 'cost'  // Track that user came from Cost tab for breadcrumb
      };
      const url = `/app/${appId}/traces?${serializeFilters(filters)}`;
      navigate(url);
    }, 200);
  };

  return (
    <ResponsiveContainer width="100%" height={280} ref={containerRef}>
        <BarChart data={rechartData} margin={{ top: 10, right: 20, left: 20, bottom: 10 }} barCategoryGap="10%">
          <CartesianGrid strokeDasharray="3 3" stroke={palette.support.gridline} opacity={0.5} />
          <XAxis
            dataKey="timestamp"
            tickFormatter={(date) => formatTimestamp(date, timeWindow)}
            tick={{ fontSize: 10, fill: palette.text.muted }}
            stroke={palette.neutrals.borderSubtle}
          />
          <YAxis
            tickFormatter={(value) => formatYAxis(value, selectedMetric)}
            tick={{ fontSize: 10, fill: palette.text.muted }}
            stroke={palette.neutrals.borderSubtle}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: palette.neutrals.panelQuaternary,
              border: `1px solid ${palette.neutrals.borderStrong}`,
              borderRadius: '4px',
              fontSize: '12px',
              padding: '8px'
            }}
            labelStyle={{ color: palette.text.heading, fontWeight: 600 }}
            itemStyle={{ color: palette.text.primary }}
            labelFormatter={(date) => formatTimestamp(date, timeWindow)}
            formatter={(value, name) => {
              // Format the value with proper units
              let displayValue;
              if (selectedMetric === 'cost_usd') {
                displayValue = `$${value.toFixed(4)}`;
              } else {
                displayValue = value.toLocaleString();
              }

              return [displayValue, name];
            }}
            cursor={false}
          />
          <Legend
            wrapperStyle={{ fontSize: '10px', paddingTop: '10px' }}
            iconType="square"
          />

          {/* Stacked bars for each agent */}
          {agentList.map((agentName) => (
            <Bar
              key={agentName}
              dataKey={agentName}
              stackId="stack"
              fill={agentColors[agentName]}
              fillOpacity={highlightedAgent === agentName ? 0.9 : 0.7}
              cursor="pointer"
              onClick={(data) => handleBarClick(agentName, data)}
              onMouseEnter={() => setHighlightedAgent(agentName)}
              onMouseLeave={() => setHighlightedAgent(null)}
              stroke="none"
              isAnimationActive={false}
              radius={[4, 4, 0, 0]}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
  );
};

export default AgentCostOverTimeChart;
